import React, {useCallback, useEffect, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import {useDropzone} from "react-dropzone";
import makeStyles from "@material-ui/styles/makeStyles";
import {Icon, IconButton, Tooltip, Typography} from "@material-ui/core";
import clsx from "clsx";
import {uid} from "react-uid";
import JsonScreenHoc from "../../../../../HocHelpers/JsonScreenHoc";
import asyncForEach from "../../../../../Utils/Form/FormDynamic/Utils/asyncForEach";
import {getFileTypesByAssociation, multiLanguageValue} from "../../../Utils/lib";
import {getParamsFromString} from "../../../Utils";
import request from "../../../../../Services/API/Requests";
import SelectType from "../../../../../Utils/Form/FormDynamic/Fields/SelectType/Select";
import {setFieldValue} from "../../../jsonScreen.slice";
import {setArrayPath} from "../../../../../Utils/Form/FormDynamic/Utils/setArrayPath";
import findByString from "../../../../../Utils/Form/FormDynamic/Utils/getValueByString";
import IconCustom from "../../../../../Utils/IconCustom/IconCustom";
import DefaultType from "../../../../../Utils/Form/FormDynamic/Fields/DefaultType/DefaultType";
import Divider from "@material-ui/core/Divider";

const useStyles = makeStyles((theme) => ({
    container: {
        border: '2px dashed #eeeeee',
        borderRadius: '2px',
        background: '#fafafa',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'

    },
    activeContainer: {
        border: `2px dashed ${theme.palette.primary.main}`,
    }
}))

function sizeValidator(file, maxSize) {
    if (maxSize !== undefined && file.size > maxSize * 1000000) {
        return {
            code: "file-too-large-custom",
            message: `File size is larger than ${maxSize} mb`
        }
    }

    return null;
}

const DropzoneComponent = (props) => {
    const {component, doActions, actionsContext, globalValues, screenName, containerArrayPath} = props;
    const styles = component.styles ? component.styles[0] : {};
    const dispatch = useDispatch();
    const classes = useStyles();
    const {properties} = component;
    const [droppedFiles, setDroppedFiles] = useState([]);
    const [onDropActions, setOnDropActions] = useState(null);
    const [fileTypes, setFileTypes] = useState([]);
    let arrayPath = setArrayPath(component.properties.path, containerArrayPath);
    const path = arrayPath ? arrayPath : component.properties.path;

    const {userLanguage} = useSelector(state => state.app);
    const multiLanguageLabel = multiLanguageValue(component.properties.label, userLanguage)
    let files = findByString(globalValues.param[screenName], path);

    useEffect(() => {
        const events = component.events || [];
        const actions = events[0] ? events[0].actions : undefined;
        actions && setOnDropActions(actions)
    }, [])

    useEffect(() => {
        async function getFileTypes() {
            try {
                const response = await request({
                    url: 'list/getData',
                    data: {
                        id: 'fileTypeDefaultList',
                        advancedFilter: component.properties.acceptedFileTypes ?
                            [{field: "id", operand: 'in', value: component.properties.acceptedFileTypes}] :
                            undefined
                    }
                });
                const fileTypes = response.content.map(item => {
                    return {
                        id: item.id,
                        name: multiLanguageValue(item.name, userLanguage),
                        association: item.association
                    }
                });
                return setFileTypes(fileTypes);
            } catch (e) {
                console.log('get file type error', e)
            }
        }

        getFileTypes()
    }, [userLanguage])

    useEffect(() => {
        setDroppedFiles(files ? Array.isArray(files) ? files : [files] : []);
    }, [JSON.stringify(files)])

    const onDrop = useCallback(async (acceptedFiles) => {
        const readyFiles = [];
        const {isArray, fileType = 'system', hideSelectType} = component.properties;
        if (onDropActions) {
            doActions(onDropActions, {
                doc: {
                    files: acceptedFiles.map(f => {
                        let objUrl = URL.createObjectURL(f)
                        return {name: f.path, content: objUrl}
                    }),
                    files1: readyFiles
                }
            })
        }
        if (!properties.path) return;

        const fileTypeExecuted = getParamsFromString(fileType, globalValues, screenName);
        await asyncForEach(acceptedFiles, async (file) => {
            let objUrl = URL.createObjectURL(file);

            //find associated file type by name
            const assocFileType = getFileTypesByAssociation(fileTypes, file);

            // Create file object
            let fileInfo = {
                name: file.name,
                type: hideSelectType ? fileTypeExecuted : assocFileType.id,
                content: objUrl,
            }
            readyFiles.push(fileInfo)
        })
        setFilesInStorage(isArray ? [...droppedFiles, ...readyFiles] : readyFiles)
    }, [droppedFiles, fileTypes, onDropActions])

    const {getInputProps, getRootProps, isDragActive, fileRejections} = useDropzone({
        onDrop,
        noClick: component.properties.type === 'drag',
        noDrag: component.properties.type === 'select',
        disabled: Boolean(component.properties.disabled),
        multiple: Boolean(component.properties.isArray),
        maxSize: properties.maxSize ? properties.maxSize * 1000000 : null,
        accept: component.properties.acceptTypes || component.properties.acceptedFileMask,
        validator: (f) => sizeValidator(f, properties.maxSize)
    });

    const setFilesInStorage = (files) => {
        let isArr = component.properties.isArray;
        if (!path) return;
        let action = {
            type: 'uploadFile',
            setToVar: path,
            multiple: component.properties.isArray,
            paramType: 'script',
            script: `{function setFiles(){ return ${JSON.stringify(isArr ? files : files[0])} }; setFiles();}`
        }

        doActions([action], actionsContext);
    }

    function onRemoveFile(index) {
        let newFiles = droppedFiles.filter((f, i) => i !== index);
        setFilesInStorage(newFiles)
    }

    function onChangeFields(value, fPath) {
        dispatch(setFieldValue({
            value,
            path: `param.${screenName}.${path}.${fPath}`
        }))
    }

    //check is height defined in %
    let isPercentageHeight = styles.height && styles.height.substr(styles.height.length - 1) === '%';

    return (
        <div
            className={styles.className}
            style={{
                width: styles.width,
                height: isPercentageHeight ? styles.height : 'auto',
                display: 'flex',
                flexDirection: 'column'
            }}>
            <section {...getRootProps()}
                     className={clsx(classes.container,
                         {[classes.activeContainer]: isDragActive})}
                     style={{
                         height: isPercentageHeight ? '100%' : styles.height,
                         width: '100%'
                     }}
            >
                <div id={'dropzoneIcon'}
                     style={{display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center'}}>
                    <IconCustom iconType={'symbols-outlined'}
                                style={{
                                    fontSize: 44,
                                    color: '#00B5F8'
                                }}
                    >
                        backup
                    </IconCustom>
                    <Typography color={'textSecondary'}>
                        {multiLanguageLabel}
                    </Typography>
                    <input
                        {...getInputProps({})}/>
                </div>
            </section>
            <div>
                {fileRejections.map(({_, errors}) => {
                    return (
                        <>
                            {errors.map(err => {
                                if (err.code === "file-too-large-custom") {
                                    return (
                                        <Typography variant={"subtitle2"} color={'secondary'}>{err.message}</Typography>
                                    )
                                }
                                if (err.code === 'file-invalid-type') {
                                    return (
                                        <Typography variant={"subtitle2"}
                                                    color={'secondary'}>{multiLanguageValue(properties.wrongTypeMessage || err.message, userLanguage)}</Typography>
                                    )
                                }
                            })}</>
                    )
                })}
            </div>
            {!properties.hideDroppedFiles && (<DroppedFilesList
                droppedFiles={droppedFiles}
                onRemoveFile={onRemoveFile}
                fileTypes={fileTypes}
                onChangeFields={onChangeFields}
                hideSelectType={component.properties.hideSelectType}
                showDescription={component.properties.showDescription}
                multiple={Boolean(component.properties.isArray)}

            />)}
        </div>

    )
}
export default JsonScreenHoc(DropzoneComponent);


const DroppedFilesList = ({droppedFiles, onRemoveFile, fileTypes, onChangeFields, hideSelectType, showDescription, multiple}) => {
    const {userLanguage} = useSelector(state => state.app)
    let values = multiple ? droppedFiles : droppedFiles[0];
    return (
        <div style={{display: 'flex', flexDirection: "column"}}>
            {
                droppedFiles.map((file, i) => {
                    let basePath = multiple ? `${i}.` : '';
                    return (
                        <div key={uid(file)}>
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',

                            }}>
                                <div style={{width: '50%', padding: 2}}>
                                    <Typography
                                        color={'textSecondary'}
                                        variant={"body2"}>{i + 1}. {multiLanguageValue(file.name, userLanguage)}</Typography>
                                    {showDescription && (
                                        <DefaultType
                                            values={values}
                                            setValue={onChangeFields}
                                            field={{
                                                path: `${basePath}description`,
                                                placeholder: 'Description...'
                                            }}
                                        />
                                    )}
                                </div>
                                <div style={{display: 'flex', width: '50%', justifyContent: 'flex-end'}}>
                                    {!hideSelectType ? <SelectType
                                        values={values}
                                        setValue={onChangeFields}
                                        showLabel={false}
                                        field={{
                                            type: 'objSelect',
                                            path: `${basePath}type`,
                                            values: fileTypes.map(v => ({value: v.id, label: v.name})),
                                            optLabel: 'label'
                                        }}
                                        fieldStyles={{
                                            fullWidth: true
                                        }}
                                    /> : <></>}
                                    <div style={{paddingLeft: 5, display: 'flex', alignItems: 'center'}}>
                                        <Tooltip title={'Remove file'}>
                                            <IconButton size={"small"}
                                                        onClick={() => onRemoveFile(i)}>
                                                <Icon fontSize={"small"}>
                                                    delete_outline
                                                </Icon>
                                            </IconButton>
                                        </Tooltip>
                                    </div>
                                </div>

                            </div>
                            {droppedFiles.length > 1 && (
                                <Divider style={{margin: '5px 0', background: '#e5e4e4'}}/>
                            )}

                        </div>
                    )
                })
            }
        </div>
    )
}